import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Alert, FlatList, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import OnboardingLayout from '@/components/OnboardingLayout';
import CircularArrowButton from '@/components/CircularArrowButton';
import { Colors } from '@/constants/Colors';
import { useLanguage } from '@/contexts/LanguageContext';
import { useOnboardingStepTracking } from '@/hooks/useOnboardingStepTracking';
import { TempOnboardingService } from '@/services/tempOnboardingService';
import { loadOnboardingClinics, type OnboardingClinic } from '@/services/onboardingClinicService';

export default function OnboardingClinicScreen() {
  const router = useRouter();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const { logCompleted } = useOnboardingStepTracking('clinic');
  const [clinics, setClinics] = useState<OnboardingClinic[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'empty' | 'error'>('loading');
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const mounted = useRef(true);
  const request = useRef(0);

  const load = useCallback(async () => {
    const version = ++request.current;
    setStatus('loading');
    try {
      const [list, data] = await Promise.all([loadOnboardingClinics(), TempOnboardingService.getData()]);
      if (!mounted.current || request.current !== version) return;
      setClinics(list);
      setSelected(list.some(item => item.id === data.selectedClinicId) ? data.selectedClinicId! : null);
      setStatus(list.length ? 'ready' : 'empty');
    } catch {
      if (mounted.current && request.current === version) {
        setClinics([]);
        setStatus('error');
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => { mounted.current = false; request.current++; };
  }, [load]);

  const choose = useCallback(async (id: number) => {
    if (busy.current) return;
    busy.current = true;
    setSaving(true);
    try {
      await TempOnboardingService.saveClinicSelection(id);
      if (mounted.current) setSelected(id);
    } catch {
      if (mounted.current) Alert.alert(t('onboarding.clinic.errorTitle'), t('onboarding.clinic.saveError'));
    } finally {
      busy.current = false;
      if (mounted.current) setSaving(false);
    }
  }, [t]);

  const next = () => {
    if (busy.current || status !== 'ready' || !clinics.some(item => item.id === selected)) return;
    logCompleted();
    router.push('/onboarding-gender');
  };
  const renderItem = useCallback(({ item }: { item: OnboardingClinic }) => (
    <TouchableOpacity accessibilityRole="radio" accessibilityState={{ checked: selected === item.id, disabled: saving }}
      aria-checked={selected === item.id} aria-disabled={saving}
      accessibilityLabel={item.name} disabled={saving} onPress={() => void choose(item.id)}
      style={[styles.card, selected === item.id && styles.selectedCard]}>
      <View style={styles.label}>
        <Text style={styles.optionText}>{item.name}</Text>
      </View>
      <View style={[styles.radio, selected === item.id && styles.selectedRadio]}>
        {selected === item.id && <View style={styles.dot} />}
      </View>
    </TouchableOpacity>
  ), [selected, saving, choose, t]);

  return (
    <OnboardingLayout progressWidth={40} title={t('onboarding.header')}>
      <View style={styles.section}>
        <View style={styles.badge}><Text style={styles.number}>4</Text></View>
        <Text style={styles.title}>{t('onboarding.clinic.title')}</Text>
        <Text style={styles.subtitle}>{t('onboarding.clinic.subtitle')}</Text>
      </View>
      {status === 'loading' ? (
        <View style={styles.state} accessibilityLiveRegion="polite">
          <ActivityIndicator color={Colors.light.primary} />
          <Text style={styles.subtitle}>{t('onboarding.clinic.loading')}</Text>
        </View>
      ) : status === 'ready' ? (
        <FlatList data={clinics} renderItem={renderItem} keyExtractor={item => String(item.id)}
          extraData={selected} contentContainerStyle={styles.list} showsVerticalScrollIndicator={false} />
      ) : (
        <View style={styles.state} accessibilityLiveRegion="polite">
          <Text style={styles.name}>{t('onboarding.clinic.errorTitle')}</Text>
          <Text style={styles.subtitle}>{status === 'empty' ? t('onboarding.clinic.empty') : t('onboarding.clinic.loadError')}</Text>
          <TouchableOpacity accessibilityRole="button" onPress={() => void load()} style={styles.retry}>
            <Text style={styles.name}>{t('onboarding.clinic.retry')}</Text>
          </TouchableOpacity>
        </View>
      )}
      <View style={[styles.footer, { paddingBottom: insets.bottom + 24 }]}>
        <Text style={styles.hint}>{t('onboarding.clinic.required')}</Text>
        <CircularArrowButton onPress={next} loading={saving} accessibilityLabel={t('onboarding.clinic.continue')}
          disabled={status !== 'ready' || selected === null || saving} />
      </View>
    </OnboardingLayout>
  );
}

const c = Colors.light;
const selection = c.onboardingSelection;
const styles = StyleSheet.create({
  section: { paddingHorizontal: 24, paddingTop: 24, gap: 8 },
  badge: { width: 40, height: 40, borderRadius: 32, backgroundColor: c.primary, alignItems: 'center', justifyContent: 'center', marginBottom: 16 },
  number: { color: c.textInverse, fontFamily: 'Sentient-Regular', fontSize: 20, lineHeight: 28 },
  title: { color: c.textPrimary, fontFamily: 'Sentient-Regular', fontSize: 24, lineHeight: 29 },
  subtitle: { color: c.textSecondary, fontFamily: 'Inter_400Regular', fontSize: 14, lineHeight: 20 },
  list: { padding: 24, gap: 12 },
  card: { padding: 20, minHeight: 64, borderRadius: 12, borderWidth: 1, borderColor: selection.border, backgroundColor: c.surface, flexDirection: 'row', alignItems: 'center', gap: 4 },
  selectedCard: { borderColor: selection.selectedBorder, backgroundColor: selection.selectedSurface },
  label: { flex: 1, gap: 4 },
  name: { color: c.textPrimary, fontFamily: 'Inter_500Medium', fontSize: 14, lineHeight: 20 },
  optionText: { color: c.textPrimary, fontFamily: 'Inter_400Regular', fontSize: 14, lineHeight: 19.6 },
  radio: { width: 24, height: 24, borderRadius: 12, borderWidth: 1, borderColor: selection.radioBorder, backgroundColor: c.surface, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  selectedRadio: { borderColor: selection.accent, borderWidth: 3 },
  dot: { width: 12, height: 12, borderRadius: 6, backgroundColor: selection.accent, flexShrink: 0 },
  state: { flex: 1, padding: 24, gap: 16, justifyContent: 'center', alignItems: 'center' },
  retry: { padding: 16, borderRadius: 12, borderWidth: 1, borderColor: c.borderDisabled },
  footer: { paddingHorizontal: 24, paddingTop: 12, flexDirection: 'row', alignItems: 'center', gap: 16 },
  hint: { flex: 1, color: c.textSecondary, fontFamily: 'Inter_400Regular', fontSize: 12, lineHeight: 18 },
});
